/**
 * Start Tournament — check-in confirmation and the draw, in one dialog.
 *
 * THE MODAL IS THE DECISION POINT for the whole night's format. Group count
 * and bracket size are not tournament settings any more (see SettingsTab):
 * they depend on who actually walked through the door, so they are chosen here
 * and sent straight to POST .../draw.
 *
 * Check-in changes are NOT staged. Each toggle writes through the same PUT the
 * Check-in tab uses and the parent reloads, so a player re-sorts into the other
 * list immediately. A staged list would mean an admin could confirm a draw
 * against a lineup that only ever existed in this component.
 *
 * Confirming does two calls in order, and the order matters:
 *
 *   1. POST confirm-lineup — DELETES everyone still not_checked_in. A no-show
 *      loses their entry entirely; that is the intended behaviour, and it is
 *      not reversible from here.
 *   2. POST draw           — allocates the groups and goes 'active'.
 *
 * The validation below duplicates the backend's refusals on purpose, so the
 * numbers are argued with before a lineup is purged rather than after. It is a
 * convenience, not the authority: every error from either call is rendered in
 * the footer, because the backend still knows things this component cannot.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api.js';
import CheckInToggle from './CheckInToggle.jsx';

/**
 * Mirrors PLAYING_STATUSES in functions/lib/draw.js — running_late counts as
 * present here for exactly the reason it does there: they are expected, just
 * not in the building yet, and the draw puts them in a group like anyone else.
 *
 * Mirrored rather than imported: nothing in src/ reaches into functions/, and
 * importing draw.js for one array would pull the round robin generator and the
 * CSPRNG shuffle into the browser bundle with it.
 */
const PLAYING_STATUSES = ['checked_in', 'running_late'];

/** Both mirror the constants of the same name in .../tournaments/[id]/draw.js. */
const MIN_GROUP_SIZE   = 2;
const MIN_BRACKET_SIZE = 2;

/** Parses a form field to a positive integer, or null. */
function toPositiveInt(value) {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Group sizes in the order the draw deals them.
 *
 * draw.js hands entry i to group i % groupCount, so the first n % g groups end
 * up one player larger. Returned largest-first for the same reason it is dealt
 * that way — "5/4/4/4" is how an admin reads a split.
 */
function groupSizes(playerCount, groupCount) {
  const base = Math.floor(playerCount / groupCount);
  const rem  = playerCount % groupCount;
  return Array.from({ length: groupCount }, (_, i) => (i < rem ? base + 1 : base));
}

/**
 * The preview line.
 *
 * Qualifiers and wildcards use the arithmetic knockout/generate.js will use on
 * the same two numbers later tonight — floor(bracketSize / groupCount) through
 * each group, and whatever that leaves over comes from the wildcard pool.
 */
function describeDraw(playerCount, groupCount, bracketSize) {
  const sizes     = groupSizes(playerCount, groupCount).join('/');
  const perGroup  = Math.floor(bracketSize / groupCount);
  const wildcards = bracketSize - perGroup * groupCount;

  return `${plural(playerCount, 'player')} → ${groupCount} `
       + `${groupCount === 1 ? 'group' : 'groups'} of ${sizes}, `
       + `${perGroup} through each`
       + (wildcards > 0 ? ` plus ${plural(wildcards, 'wildcard')}` : '');
}

/**
 * Every reason the draw would be refused, in the order an admin would fix them.
 *
 * Returns [] when the numbers are good. boardCount is skipped when the venue
 * has not set one, matching draw.js — no setting means no cap, not a cap of
 * zero.
 */
function validate({ playerCount, groupCount, bracketSize, boardCount }) {
  const errors = [];

  if (playerCount < MIN_GROUP_SIZE) {
    errors.push(`The draw needs at least ${MIN_GROUP_SIZE} checked-in players — `
              + `there ${playerCount === 1 ? 'is' : 'are'} ${playerCount}.`);
  }

  if (groupCount === null)  errors.push('Choose how many groups to split into.');
  if (bracketSize === null) errors.push('Choose a knockout bracket size.');

  if (groupCount !== null && boardCount !== null && groupCount > boardCount) {
    errors.push(`${groupCount} groups is more than the venue's `
              + `${plural(boardCount, 'board')} — group 1 plays board 1, and so on.`);
  }

  if (bracketSize !== null && bracketSize < MIN_BRACKET_SIZE) {
    errors.push(`A knockout needs at least ${MIN_BRACKET_SIZE} players.`);
  }

  if (groupCount !== null && bracketSize !== null && bracketSize < groupCount) {
    errors.push(`A bracket of ${bracketSize} is smaller than ${groupCount} groups — `
              + 'every group must send at least one player to the knockout.');
  }

  if (bracketSize !== null && bracketSize > playerCount) {
    errors.push(`A bracket of ${bracketSize} is more than the `
              + `${plural(playerCount, 'checked-in player')}. Choose a smaller bracket `
              + 'or check in more players.');
  }

  if (groupCount !== null && playerCount < groupCount * MIN_GROUP_SIZE) {
    errors.push(`${plural(playerCount, 'checked-in player')} cannot fill ${groupCount} `
              + `groups of at least ${MIN_GROUP_SIZE}. Choose fewer groups or check in `
              + 'more players.');
  }

  return errors;
}

/* ------------------------------------------------------------------------- */

/** One name and its toggle, in either list. */
function CheckInRow({ entry, busy, disabled, onChange }) {
  return (
    <li className="hd-checkin-row">
      <span>
        {entry.display_name}
        {entry.is_guest === 1 && <span className="hd-guest-tag">guest</span>}
      </span>
      <CheckInToggle
        status={entry.check_in_status}
        busy={busy}
        disabled={disabled}
        onChange={onChange}
      />
    </li>
  );
}

/* ------------------------------------------------------------------------- */

export default function StartTournamentModal({
  venueId, tournamentId, entries, onChanged, onClose, onDrawn,
}) {
  const [defaults, setDefaults] = useState({
    loading: true, boardCount: null, defaultBracketSize: null,
  });

  const [groupCount, setGroupCount]   = useState('');
  const [bracketSize, setBracketSize] = useState('');

  const [busyId, setBusyId] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);

  // Set once confirm-lineup has run, so a failure at the draw step can say so.
  // The purge is a DELETE and cannot be walked back by closing this dialog.
  const [purged, setPurged] = useState(false);

  const panel = useRef(null);

  const base = `/api/venues/${venueId}/tournaments/${tournamentId}`;

  // The bracket field is prefilled from the venue default, and board_count
  // rides along for the cap in validate(). Fetched on open rather than with the
  // page: nothing else on the page needs either number.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data, error: err } = await apiGet(
        `/api/venues/${venueId}/settings/draw-defaults`
      );
      if (cancelled) return;

      // A failed read is not fatal — the fields still work, the bracket one
      // just starts empty and the board cap goes unchecked here. The draw
      // still applies it server-side either way.
      setDefaults({
        loading:            false,
        boardCount:         data?.boardCount ?? null,
        defaultBracketSize: data?.defaultBracketSize ?? null,
      });

      if (err) {
        setError(`Could not read the venue's draw defaults: ${err}`);
      } else if (data?.defaultBracketSize != null) {
        // Prefill only, and only on open. Group count gets no default at all —
        // it is the one number that should be chosen fresh every night.
        setBracketSize(String(data.defaultBracketSize));
      }
    })();

    return () => { cancelled = true; };
  }, [venueId]);

  // Escape closes, but never mid-write: a dialog that vanishes while two
  // sequential POSTs are in flight leaves the admin with no idea which landed.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => { panel.current?.focus(); }, []);

  /**
   * The two lists, by name.
   *
   * /entries comes back in registration order, which is the right order for
   * the Entries tab and the wrong one here — an admin at the door is looking a
   * person UP, not reading down the list. Sorted in this component rather than
   * by changing the endpoint, because the tabs that already read it want the
   * order they have.
   */
  const [checkedIn, notCheckedIn] = useMemo(() => {
    const byName = (a, b) =>
      (a.display_name ?? '').localeCompare(b.display_name ?? '', undefined, {
        sensitivity: 'base',
      });

    return [
      entries.filter((e) => PLAYING_STATUSES.includes(e.check_in_status)).sort(byName),
      entries.filter((e) => !PLAYING_STATUSES.includes(e.check_in_status)).sort(byName),
    ];
  }, [entries]);

  const parsedGroups  = toPositiveInt(groupCount);
  const parsedBracket = toPositiveInt(bracketSize);

  const problems = validate({
    playerCount: checkedIn.length,
    groupCount:  parsedGroups,
    bracketSize: parsedBracket,
    boardCount:  defaults.boardCount,
  });

  // Shown whenever the split itself is describable. A bracket that is too big
  // still previews — the problem list below says why it would be refused, and
  // seeing the groups it would have made is what tells an admin which of the
  // two numbers to change.
  const preview = parsedGroups !== null && parsedBracket !== null
                  && checkedIn.length >= parsedGroups
    ? describeDraw(checkedIn.length, parsedGroups, parsedBracket)
    : null;

  const setStatus = async (entry, status) => {
    setBusyId(entry.id);
    setError(null);
    const { error: err } = await apiPut(`${base}/check-in`, { entryId: entry.id, status });
    setBusyId(null);
    if (err) { setError(err); return; }
    // The reload is what re-sorts them into the other list — this component
    // holds no copy of the lineup to move them within.
    await onChanged();
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);

    const lineup = await apiPost(`${base}/confirm-lineup`, {});
    if (lineup.error) {
      setBusy(false);
      setError(lineup.error);
      return;
    }

    setPurged(true);

    const draw = await apiPost(`${base}/draw`, {
      groupCount:  parsedGroups,
      bracketSize: parsedBracket,
    });

    if (draw.error) {
      setError(draw.error);
      // The lists behind this error are now wrong — the no-shows are gone.
      await onChanged();
      setBusy(false);
      return;
    }

    await onChanged();
    setBusy(false);
    onDrawn();
  };

  const blocked = busy || defaults.loading || problems.length > 0;

  return (
    <div
      className="hd-modal-veil"
      onMouseDown={(event) => {
        // Only a click on the veil itself, and never mid-write.
        if (event.target === event.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="hd-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hd-start-title"
        tabIndex={-1}
        ref={panel}
      >
        <div className="hd-modal-head">
          <h2 className="hd-section-title" id="hd-start-title" style={{ margin: 0 }}>
            Start tournament
          </h2>
          <button
            type="button"
            className="hd-btn hd-btn-quiet hd-btn-sm"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>

        <div className="hd-modal-body hd-stack">
          <p className="hd-hint" style={{ margin: 0 }}>
            Confirming removes everyone still marked "Not in" from the
            tournament entirely, then runs the draw. Running late counts as
            checked in — they get a group, and their matches go to the end of
            the order.
          </p>

          <div>
            <h3 className="hd-section-title">Checked in ({checkedIn.length})</h3>
            {checkedIn.length === 0
              ? <p className="hd-meta">Nobody has checked in yet.</p>
              : (
                <ul className="hd-checkin-list">
                  {checkedIn.map((entry) => (
                    <CheckInRow
                      key={entry.id}
                      entry={entry}
                      busy={busyId === entry.id}
                      disabled={busy}
                      onChange={(status) => setStatus(entry, status)}
                    />
                  ))}
                </ul>
              )}
          </div>

          <div>
            <h3 className="hd-section-title">Not checked in ({notCheckedIn.length})</h3>
            {notCheckedIn.length === 0
              ? <p className="hd-meta">Everyone is accounted for.</p>
              : (
                <>
                  <p className="hd-hint" style={{ marginTop: 0 }}>
                    These entries are deleted when you confirm.
                  </p>
                  <ul className="hd-checkin-list">
                    {notCheckedIn.map((entry) => (
                      <CheckInRow
                        key={entry.id}
                        entry={entry}
                        busy={busyId === entry.id}
                        disabled={busy}
                        onChange={(status) => setStatus(entry, status)}
                      />
                    ))}
                  </ul>
                </>
              )}
          </div>

          <h3 className="hd-section-title">Format</h3>

          <div className="hd-fields">
            <div className="hd-field">
              <label className="hd-label" htmlFor="hd-start-groups">Groups</label>
              <input
                id="hd-start-groups"
                className="hd-input"
                type="number"
                min="1"
                value={groupCount}
                disabled={busy}
                onChange={(e) => setGroupCount(e.target.value)}
              />
              <span className="hd-hint">
                {defaults.boardCount !== null
                  ? `Up to ${plural(defaults.boardCount, 'board')} at this venue.`
                  : 'Group 1 plays board 1, group 2 board 2, and so on.'}
              </span>
            </div>

            <div className="hd-field">
              <label className="hd-label" htmlFor="hd-start-bracket">Bracket size</label>
              <input
                id="hd-start-bracket"
                className="hd-input"
                type="number"
                min={MIN_BRACKET_SIZE}
                value={bracketSize}
                disabled={busy}
                onChange={(e) => setBracketSize(e.target.value)}
              />
              <span className="hd-hint">
                {defaults.defaultBracketSize !== null
                  ? `The venue's default is ${defaults.defaultBracketSize}.`
                  : 'How many players go through to the knockout.'}
              </span>
            </div>
          </div>

          {preview && <p className="hd-draw-preview" role="status">{preview}</p>}
        </div>

        <div className="hd-modal-foot">
          {problems.length > 0 && (
            <ul className="hd-modal-problems">
              {problems.map((problem) => <li key={problem}>{problem}</li>)}
            </ul>
          )}

          {error && (
            <div className="hd-badge hd-badge-danger hd-modal-error" role="alert">
              {error}
              {purged && (
                <>
                  {' '}The no-shows have already been removed — only the numbers
                  above need changing, not the check-in lists.
                </>
              )}
            </div>
          )}

          <div className="hd-row">
            <button
              type="button"
              className="hd-btn hd-btn-primary"
              disabled={blocked}
              onClick={confirm}
            >
              {busy ? 'Drawing…' : 'Confirm draw'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
